import productReviews from "@faire/web-api/api/product/productToken/brand-reviews-summary/get";
import getProductRelatedSearches from "@faire/web-api/api/seo/product-related-searches/productToken/get";
import { IBadge } from "@faire/web-api/indigofair/badges/IBadge";
import { IDisclaimer } from "@faire/web-api/indigofair/data/IDisclaimer";
import { IGetProductBrandReviewsSummaryResponse } from "@faire/web-api/indigofair/data/IGetProductBrandReviewsSummaryResponse";
import { IInformationMessage } from "@faire/web-api/indigofair/data/IInformationMessage";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { IProductsResponse } from "@faire/web-api/indigofair/data/IProductsResponse";
import { IGetRelatedSearchesResponse } from "@faire/web-api/indigofair/seo/IGetRelatedSearchesResponse";
import { IProductVideo } from "@faire/web-api/indigofair/video/IProductVideo";
import { makeObservable } from "@faire/web/common/makeObservable";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { action, computed, observable } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";

import { ISamplesBannerDetails } from "@faire/retailer/components/Modals/SamplesRequestModal/RequestSamplesCTA";
import {
  buildBrandReviewSummaryFromProductBrandReview,
  buildProductReviewSummaryFromProductBrandReview,
  IReviewSummary,
} from "@faire/retailer/lib/buildReviewSummary";
import { fromPromise_resolve } from "@faire/retailer/lib/mobxHelpers";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import { ProductCacheStore } from "@faire/retailer/stores/domain/ProductCacheStore";

export interface ShowroomProductData {
  showVerticalImagery: boolean | undefined;
  isMidPricePointApparelProduct: boolean | undefined;
}

/**
 * @deprecated This is in the process of being migrated to react query.
 * @see useProductState
 */
export class ProductState {
  @observable
  private productRelatedSearchesResponse?: IPromiseBasedObservable<IGetRelatedSearchesResponse>;

  @observable
  private reviewsSummaryResponse?: IPromiseBasedObservable<
    IGetProductBrandReviewsSummaryResponse | undefined
  >;

  @observable
  private clientProductResponseData?: IPromiseBasedObservable<
    IProductPageResponse | undefined
  >;

  @observable
  clientProductResponseDataUpdatedAt: number | undefined = undefined;

  private readonly productToken: string;

  constructor(
    productToken: string,
    /**
     * Optional prefetched product response, to seed the state with.
     */
    productResponse?: IProductPageResponse
  ) {
    makeObservable(this);
    this.productToken = productToken;
    if (isNotUndefined(productResponse)) {
      this.clientProductResponseData = fromPromise_resolve(productResponse);
      this.clientProductResponseDataUpdatedAt = Date.now();
    }
  }

  @action
  init = async () => {
    if (!this.clientProductResponseData) {
      this.clientProductResponseData = fromPromise(
        ProductCacheStore.get()
          .fetchLeanProduct(this.productToken)
          .then((value) => {
            PerformanceMarker.get().mark(PageLoadEvent.primaryFetchEnd);
            this.clientProductResponseDataUpdatedAt = Date.now();
            return value;
          }),
        this.clientProductResponseData
      );
    }

    if (!this.reviewsSummaryResponse) {
      this.fetchReviewsSummary();
    }

    if (!this.productRelatedSearchesResponse && this.productToken) {
      this.productRelatedSearchesResponse = fromPromise(
        getProductRelatedSearches(this.productToken)
      );
    }

    try {
      await this.clientProductResponseData;
    } catch {
      return;
    }
  };

  private fetchReviewsSummary = () => {
    this.reviewsSummaryResponse = fromPromise(
      productReviews(this.productToken)
    );
  };

  @computed
  private get reviewsSummary():
    | IGetProductBrandReviewsSummaryResponse
    | undefined {
    return this.reviewsSummaryResponse?.case({
      fulfilled: (response) => response,
    });
  }

  @computed
  get productReviewSummary(): IReviewSummary | undefined {
    return buildProductReviewSummaryFromProductBrandReview(this.reviewsSummary);
  }

  @computed
  get brandReviewSummary(): IReviewSummary | undefined {
    return buildBrandReviewSummaryFromProductBrandReview(this.reviewsSummary);
  }

  private get resolvedProductResponse() {
    return this.clientProductResponseData?.case({
      fulfilled: (response) => response,
    });
  }

  @computed
  get productRelatedSearches() {
    return this.productRelatedSearchesResponse?.case({
      fulfilled: (response) => response.queries,
    });
  }

  @computed
  get brand(): IProductPageResponse.IPdpBrand | undefined {
    return this.clientProductResponseData?.case({
      pending: (stale) => stale?.brand,
      fulfilled: (resp) => resp?.brand,
    });
  }

  @computed
  get isBrandActive(): boolean {
    return !!this.brand?.active;
  }

  @computed
  get disclaimers(): IDisclaimer[] {
    return this.resolvedProductResponse?.product?.messages?.disclaimers ?? [];
  }

  @computed
  get videos(): IProductVideo[] {
    return this.resolvedProductResponse?.product?.visuals?.videos ?? [];
  }

  @computed
  get product(): IProductPageResponse.IPdpProduct | undefined {
    return this.clientProductResponseData?.case({
      pending: (stale) => stale?.product,
      fulfilled: (resp) => resp?.product,
    });
  }

  @computed
  get showroomData(): ShowroomProductData | undefined {
    return {
      isMidPricePointApparelProduct:
        this.resolvedProductResponse?.product?.purchase_information
          ?.is_mid_price_point_brand_apparel_product,
      showVerticalImagery:
        this.resolvedProductResponse?.product?.visuals?.show_vertical_image,
    };
  }

  @computed
  get badges(): IBadge[] | undefined {
    return this.resolvedProductResponse?.product?.visuals?.badges;
  }

  @computed
  get importShipmentInfo() {
    return this.resolvedProductResponse?.product?.purchase_information
      ?.import_shipment_info;
  }

  @computed
  get infoTexts(): IInformationMessage[] | undefined {
    return this.resolvedProductResponse?.product?.messages?.info_texts;
  }

  @computed
  get isLoading(): boolean {
    return (
      this.clientProductResponseData?.case({
        fulfilled: () => false,
        pending: () => true,
        rejected: () => false,
      }) ?? true
    );
  }

  @computed
  get productAttributeTags():
    | Array<IProductsResponse.IProductAttributeTag>
    | undefined {
    return this.resolvedProductResponse?.product?.details?.attribute_tags;
  }

  @computed
  get samplesBannerDetails(): ISamplesBannerDetails | undefined {
    if (
      !this.resolvedProductResponse?.product?.brand_sample_program
        ?.is_samples_enabled_for_session ||
      !this.resolvedProductResponse?.product?.brand_sample_program?.banner
    ) {
      return;
    }

    const { title, message, button_text, cta_link, cta_text } =
      this.resolvedProductResponse.product.brand_sample_program.banner;

    if (
      title === undefined ||
      message === undefined ||
      button_text === undefined ||
      cta_link === undefined ||
      cta_text === undefined
    ) {
      return;
    }

    return {
      title,
      message,
      buttonText: button_text,
      ctaLink: cta_link,
      ctaText: cta_text,
    };
  }
}
